import { AnimationEvent, ReactNode, useEffect, useRef, useState } from 'react';

import { TOAST_EXIT_DURATION } from '../constants/toastTiming';
import { UseExitAnimationParams } from '../types/UseExitAnimationParams';

export const useExitAnimation = ({
  children,
  open,
}: UseExitAnimationParams): {
  content: ReactNode;
  handleAnimationEnd: (event: AnimationEvent<HTMLElement>) => void;
  visible: boolean;
} => {
  const [previousOpen, setPreviousOpen] = useState(open);
  const [exiting, setExiting] = useState(false);
  const lastChildren = useRef(children);

  const handleAnimationEnd = (event: AnimationEvent<HTMLElement>): void => {
    // Animations on the toast's children bubble up here too
    if (!open && event.target === event.currentTarget) {
      setExiting(false);
    }
  };

  useEffect((): void => {
    if (open) {
      lastChildren.current = children;
    }
  });

  useEffect((): (() => void) | undefined => {
    if (!exiting) {
      return undefined;
    }

    // jsdom never fires animationend, and a missed event must not strand the toast
    const fallback = setTimeout((): void => {
      setExiting(false);
    }, TOAST_EXIT_DURATION);

    return (): void => {
      clearTimeout(fallback);
    };
  }, [exiting]);

  // Set during render: an effect would unmount the toast for a frame before it animates out
  if (open !== previousOpen) {
    setPreviousOpen(open);
    setExiting(!open);
  }

  return {
    content: open ? children : lastChildren.current,
    handleAnimationEnd,
    visible: open || exiting,
  };
};
